import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import HeaderImg from "../image/coverletter_secondpage.png";
import SignImgMadhav from "../image/guidsign1.png";
import SignRamesh from "../image/guidstamp.png";
import GuidImg from "../image/guidlinesimgsign.png";
import moment from "moment";


let config = {
  margin: 35,
  fontSize: 10.5,
  headerHeight: 90,
  footerHeight: 0,
};

// Helper: Add header image to page
function addPageHeader(doc, pageNumber) {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Add header image using config.headerHeight
  doc.addImage(HeaderImg, 'PNG', config.margin, 10, pageWidth - (config.margin * 2), config.headerHeight - 20);
}

// Helper: Add footer image and page number
function addPageFooter(doc, pageNumber, totalPages) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  // Add footer image using config.footerHeight
  const footerY = pageHeight - 15;
  doc.addImage(HeaderImg, 'PNG', config.margin, pageHeight - config.footerHeight + 10, pageWidth - config.margin * 2, config.footerHeight - 20 );

  // Page number (left)
  doc.setFont("Times", "bold");
  doc.setFontSize(config.fontSize - 1);
  doc.text(`Page ${pageNumber} of ${totalPages}`, config.margin, footerY);
  // Format text based on page number
  let formatText = "";
  if (pageNumber === 1) {
    formatText = "Format No. AIPL-FM-HR-1, Rev.1 Rev. Date: 05.08.2023";
  } else if (pageNumber >= 2 && pageNumber <= 4) {
    formatText = "Format No. AIPL-FM-HR-2, Rev.1 Rev. Date: 05.08.2023";
  } else if (pageNumber >= 5 && pageNumber <= 7) {
    formatText = "Format No. AIPL/DOC/INSP/GL/1, Rev-7, Date: 26.07.2024";
  } else if (pageNumber >= 8 && pageNumber <= 11) {
    formatText = "HR/AIPL/EHB_02";
  }
  // Right side format text (only if exists)
  if (formatText) {
    doc.text(formatText, pageWidth - config.margin, footerY, { align: "right" });
  }
}

// Helper: Add headers and footers to all pages
function addHeadersAndFooters(doc) {
  const totalPages = doc.getNumberOfPages();
  
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addPageHeader(doc, page);
    addPageFooter(doc, page, totalPages);
  }
}

// MAIN FUNCTION: Works for single or multiple inspectors
export async function generateInspectorSignReport(data) {
  const dataList = Array.isArray(data) ? data : [data];

  for (const emp of dataList) {
    const doc = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    const empName = emp.inspectorName || "";
    const inspectSign = emp.usersign || "";
    const employeePolicDate = emp.policyDate ? moment(emp.policyDate).format("DD-MM-YYYY") || "" : "";

    // Page 1: Conflict of Interest
    generateConflictOfInterestPage(doc, empName, inspectSign, employeePolicDate);

    // Page 2: Non-Disclosure
    doc.addPage();
    generateNonDisclosurePage(doc, empName, inspectSign, employeePolicDate);

    // Page 3: Minimum Guidelines
    doc.addPage();
    generateMinimumGuidelinesPage(doc);

    // Page 4: Employee Handbook
    doc.addPage();
    generateEmployeeHandbookPage(doc, empName, inspectSign, employeePolicDate);

    // Page 5: IMS-QEHS Policy
    doc.addPage();
    generateIMSQEHSPolicyPage(doc, empName, inspectSign, employeePolicDate);

    // Add headers and footers to all pages
    addHeadersAndFooters(doc);
    // Save PDF
    doc.save(`${empName}_sign_report.pdf`);
  }
}

// PAGE 1 CONTENT
const generateConflictOfInterestPage = (doc, employeeName = "", inspectSign, inspectorpolicDate) => {
  // Start content below header using config.headerHeight
  let y = config.headerHeight + 10;
  
  // 🔹 Add Center Heading
  doc.setFont("Times", "bold");
  doc.setFontSize(16);

  const headingText = "CONFLICT OF INTEREST AND IMPARTIALITY AGREEMENT";
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = doc.getTextWidth(headingText);

  doc.text(headingText, (pageWidth - textWidth) / 2, y);

  // 🔹 Draw border line under heading
  doc.setLineWidth(1);
  doc.line(40, y + 10, pageWidth - 40, y + 10);

  // Move cursor down
  y += 40;

  doc.setFont("Times", "normal");
  doc.setFontSize(12);

  const paragraph1 = `This Agreement is between AIPL and ${employeeName} an individual ("EMPLOYEE") working at DYC Global Inspections Private Limited.`;
  const paragraph2 = `WHEREAS, AIPL has committed to Impartiality in its decisions related to the inspection and expediting activities carried out.`;

  doc.text(paragraph1, 40, y, { maxWidth: 520 });
  y += 50;
  doc.text(paragraph2, 40, y, { maxWidth: 520 });
  y += 40;

  const drawSection = (title, bullets) => {
    doc.setFont("Times", "bold");
    doc.text(title, 40, y);
    y += 15;

    doc.setFont("Times", "normal");

    autoTable(doc, {
      startY: y,
      margin: { left: 40 },
      theme: "plain",
      styles: {
        font: "Times",
        fontSize: 11,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 480 },
      },
      body: bullets.map((b) => ["•", b]),
    });

    y = doc.lastAutoTable.finalY + 15;
  };

  drawSection("SELF INTEREST THREATS", [
    "The EMPLOYEE shall not act on their own interest nor review the work done by themselves or by their colleagues.",
  ]);

  drawSection("INTIMIDATION THREATS", [
    "The EMPLOYEE shall not get influenced by a close relationship with a person who is a service provider to our Client.",
    "The EMPLOYEE shall not act inadequately due to intimidation by the Client.",
    "The EMPLOYEE shall not get influenced by being or believing that they are being, openly or secretly coerced by other interested parties.",
    "The EMPLOYEE shall not misuse their position, force anyone to provide, for themselves or others, money/benefits not due.",
    "The EMPLOYEE shall not work under pressure due to late scheduling of activities.",
  ]);

  drawSection("ADVOCACY THREATS", [
    "The EMPLOYEE shall not act in support of or in opposition to a given organisation which is at the same time its customer, in the resolution of a dispute or litigation.",
  ]);

  drawSection("FINANCIAL INTERESTS", [
    "The EMPLOYEE shall not risk the inspection contract due to competition threats.",
    "The AIPL shall not link remuneration of its EMPLOYEEs to the volume or outcome of inspections performed.",
    "The EMPLOYEE shall not give conflicting representation of AIPL and/or its accreditation within other areas of the group.",
    "The AIPL nor its EMPLOYEE shall not give any payment as sales commission or inducement for the referral of new clients to the AIPL or other areas of the business.",
  ]);

    // Signature Section (ensure it's above footer)
    const pageHeight = doc.internal.pageSize.getHeight();
    // Signature Section (ensure it's above footer)
    y = Math.min(doc.lastAutoTable.finalY + 40, pageHeight - config.footerHeight - 90);

    /// Set font for names
    doc.setFont("Times", "bold");
    doc.text("AIPL", 130, y);
    doc.text("Ramesh S Jadhav", 110, y + 20);
    doc.addImage(SignRamesh, "PNG", 100, y + 25, 120, 55);
    doc.text(inspectorpolicDate || "", 120, y + 90);

    doc.text("EMPLOYEE", 360, y);
    doc.text(employeeName || "_____", 350, y + 20);
    inspectSign ? doc.addImage(inspectSign, "PNG", 350, y + 35, 100, 40) : doc.text("(Signature)", 350, y + 40);
    doc.text(inspectorpolicDate || "", 360, y + 95);
    doc.setFont("Times", "normal");
};

// PAGE 2 CONTENT
const generateNonDisclosurePage = (doc, employeeName = "",inspectSign, inspectorpolicDate) => {
    let y = config.headerHeight + 10; // Start below header using config
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const alpha = (i) => String.fromCharCode(65 + i);
    const ensurePage = (space = 20) => {
        // Account for header and footer space using config
        if (y + space > pageHeight - config.footerHeight) {
            doc.addPage();
            y = config.headerHeight + 10; // Start below header on new page
        }
    };

    /* HEADING */
    const headingText = "NON-DISCLOSURE AND INTELLECTUAL PROPERTY\nRIGHTS AGREEMENT";

    doc.setFont("Times", "bold");
    doc.setFontSize(16);

    headingText.split("\n").forEach((line) => {
        const w = doc.getTextWidth(line);
        doc.text(line, (pageWidth - w) / 2, y);
        y += 20;
    });

    doc.setLineWidth(1);
    doc.line(40, y, pageWidth - 40, y);
    y += 30;

    /* INTRO PARAGRAPHS */
    doc.setFont("Times", "normal");
    doc.setFontSize(12);

    const paragraphs = [
        `This Agreement is between AIPL and ${employeeName} an individual ("EMPLOYEE") working at DYC Global Inspections Private Limited. WHEREAS, AIPL has developed through substantial effort, research, time, and expense certain inventions, design concepts, methodologies, technical know-how, copyrightable material and trade secrets directed and related to ("INFORMATION");`,
        `WHEREAS, AIPL desires to disclose the INFORMATION on a confidential basis to EMPLOYEE solely for the purposes of evaluating the INFORMATION for possible future business arrangements; and WHEREAS, AIPL wishes to maintain the confidentiality of the INFORMATION and the protection of AIPL'S intellectual property rights. NOW, THEREFORE, in consideration of the mutual promises, covenants, and conditions contained herein, the sufficiency of which is hereby acknowledged, the parties agree as follows:`,
    ];

    paragraphs.forEach((p) => {
        doc.splitTextToSize(p, 520).forEach((line) => {
            ensurePage(15);
            doc.text(line, 40, y);
            y += 15;
        });
        y += 10;
    });

    /* I. CONFIDENTIAL INFORMATION */
    ensurePage(30);
    doc.setFont("Times", "bold");
    doc.text("I. CONFIDENTIAL INFORMATION", 40, y);
    y += 20;

    doc.setFont("Times", "normal");
    const sectionOne = [
        "AIPL agrees to disclose INFORMATION to EMPLOYEE to facilitate possible future business dealings between the parties.",
        "EMPLOYEE agrees to receive such INFORMATION and to refrain from copying, disclosing, using, selling, or offering for sale any and all of said INFORMATION, other than at the request of AIPL, with the exceptions as provided in paragraph C herein. EMPLOYEE agrees to keep confidential and refrain from disclosing any and all of the INFORMATION, and to take all necessary and reasonable steps to prevent unauthorized disclosure or use of any and all of the INFORMATION.",
        "Notwithstanding paragraph B, EMPLOYEE shall not be liable for disclosure or use of INFORMATION only if, and only to the extent that, said INFORMATION was in the public domain at the time it was disclosed by AIPL, or was known to and recorded in writing by EMPLOYEE prior to the time of disclosure by AIPL, or is received from a third party or passes into the public domain without breach of this Agreement. With respect to any INFORMATION known by EMPLOYEE prior to the time of disclosure by AIPL that EMPLOYEE believes to constitute the INFORMATION, or any portion thereof, EMPLOYEE shall disclose to AIPL an adequate written description of the INFORMATION within fourteen (14) days of the disclosure by AIPL.",
        "This is not an offer for sale or license. No right or license is granted by AIPL to EMPLOYEE in connection with the technical information or inventions disclosed under this agreement. All documents or materials constituting the INFORMATION and all reproductions thereof shall at all times remain the sole property of AIPL and shall promptly be returned by EMPLOYEE upon request.",
        "This Agreement shall remain in force in spite of disclosure of the INFORMATION by AIPL in the form of patent applications, copyright applications, or other disclosures by AIPL.",
    ];

    sectionOne.forEach((text, i) => {
        doc.splitTextToSize(text, 460).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? `${alpha(i)}. ${line}` : line, idx === 0 ? 50 : 70, y);
            y += 15;
        });
        y += 5;
    });

    /* II. RESTRICTIONS */
    ensurePage(100);
    doc.setFont("Times", "bold");
    doc.text("II. RESTRICTIONS", 40, y);
    y += 20;

    doc.setFont("Times", "normal");

    const sectionTwo = [
        {
            text: "Except for the express written consent of AIPL, EMPLOYEE agrees:",
            sub: [
                "Not to use or disclose to another person or entity any confidential information of AIPL;",
                "Not to make, or cause to be made, any copies, facsimiles or other reproductions including data files of any documents containing confidential information of AIPL;",
                "To use all other reasonable means to maintain the secrecy and confidentiality of the confidential information of AIPL.",
            ],
        },
        {
            text: "EMPLOYEE further agrees, at the request of AIPL:",
            sub: [
                "To immediately return to AIPL all of the items in the possession of EMPLOYEE which relate to or which disclose in whole or in part any confidential information of AIPL;",
                "To refrain from using or disclosing to any other person or entity any confidential information of AIPL.",
            ],
        },
    ];

    sectionTwo.forEach((item, i) => {
        doc.splitTextToSize(item.text, 460).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? `${alpha(i)}. ${line}` : line, idx === 0 ? 50 : 70, y);
            y += 15;
        });

        item.sub.forEach((sub, j) => {
            doc.splitTextToSize(sub, 420).forEach((line, k) => {
                ensurePage(15);
                doc.text(k === 0 ? `${j + 1}. ${line}` : line, k === 0 ? 80 : 100, y);
                y += 15;
            });
        });

        y += 10;
    });

    /* III. INTELLECTUAL PROPERTY */
    ensurePage(30);
    doc.setFont("Times", "bold");
    doc.text("III. INTELLECTUAL PROPERTY", 40, y);
    y += 20;

    /* A. Title and Copyright Assignment */
    doc.text("A. Title and Copyright Assignment", 50, y);
    y += 20;

    doc.setFont("Times", "normal");
    const sectionThreeA = [
        "All products and results of EMPLOYEE'S services rendered hereunder (the 'Work') are works made for hire. EMPLOYEE acknowledges and agree that the Work (and all rights therein, including, without limitation, copyrights) belongs to and shall be the sole and exclusive property of AIPL.",
        "Notwithstanding the foregoing, EMPLOYEE also hereby assigns and transfers to AIPL, its successors and assigns, the entire right, title, and interest in and to all copyrights in the Work; all registrations and copyright applications relating thereto and all renewals and extensions thereof; all works based upon, derived from, or incorporating the Work; all income, royalties, damages, claims and payments now or hereafter due or payable with respect thereto; all causes of action, either in law or in equity for past, present, or future infringement based on the copyrights; and all rights corresponding to the foregoing throughout the world.",
        "If the Work is one to which the provisions of Indian Contract Act apply, EMPLOYEE hereby waives and appoints AIPL to assert on EMPLOYEE'S behalf EMPLOYEE'S moral rights or any equivalent rights regarding the form or extent of any alteration to the Work.",
        "EMPLOYEE agrees to execute all papers and to perform such other proper acts as AIPL may deem necessary to secure for AIPL or its designee the rights herein assigned.",
    ];

    sectionThreeA.forEach((text, i) => {
        doc.splitTextToSize(text, 450).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? `${i + 1}. ${line}` : line, idx === 0 ? 60 : 80, y);
            y += 15;
        });
        y += 5;
    });

    /* B. Patent Assignment */
    ensurePage(30);
    doc.setFont("Times", "bold");
    doc.text("B. Patent Assignment", 50, y);
    y += 20;

    doc.setFont("Times", "normal");
    const sectionThreeB = [
        "EMPLOYEE may invent new, original, and ornamental or useful inventions in the course of or related to EMPLOYEE'S business relationship with AIPL (\"the Inventions\").",
        "EMPLOYEE hereby assigns and/or transfers to AIPL, its successors or assigns, the entire right, title, and interest in and to said Inventions, and any patent and patent applications deriving therefrom for any such invention in India and throughout the world, including the right to file foreign applications directly in the name of AIPL and to claim priority rights under international conventions, treaties, or otherwise; and to cooperate with AIPL as may be necessary for obtaining, sustaining, reissuing, or enforcing said patents and applications.",
        "Notwithstanding the above, EMPLOYEE shall not assign and/or transfer any invention for which no confidential information of AIPL was used, unless the invention results from any work performed by EMPLOYEE for AIPL.",
    ];

    sectionThreeB.forEach((text, i) => {
        doc.splitTextToSize(text, 450).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? `${i + 1}. ${line}` : line, idx === 0 ? 60 : 80, y);
            y += 15;
        });
        y += 5;
    });

    /* C. Ownership of Trademarks */
    ensurePage(50);
    doc.setFont("Times", "bold");
    doc.text("C. Ownership of Trademarks", 50, y);
    y += 20;

    doc.setFont("Times", "normal");
    const trademarkText = "EMPLOYEE hereby acknowledges that AIPL shall retain all right, title, and interest in all trademarks, trade dress, and goodwill that results from the INFORMATION or any use or offer to sell thereof.";

    doc.splitTextToSize(trademarkText, 480).forEach((line) => {
        ensurePage(15);
        doc.text(line, 60, y);
        y += 15;
    });

    // Signature Section (ensure it's above footer)
    ensurePage(100);
    y+= 40;
    /// Set font for names
    doc.setFont("Times", "bold");
    doc.text("AIPL", 130, y);
    doc.text("Ramesh S Jadhav", 110, y + 20);
    doc.addImage(SignRamesh, "PNG", 100, y + 25, 120, 55);
    doc.text(inspectorpolicDate || "", 120, y + 90);


    doc.text("EMPLOYEE", 360, y);
    doc.text(employeeName || "_____", 350, y + 20);
    inspectSign ? doc.addImage(inspectSign, "PNG", 350, y + 35, 100, 40) : doc.text("(Signature)", 350, y + 40);
    doc.text(inspectorpolicDate || "", 360, y + 95);
    doc.setFont("Times", "normal");
};


// page 3
const generateMinimumGuidelinesPage = (doc) => {
    let y = config.headerHeight + 10; // Start below header using config
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const lineHeight = 15;

    const ensurePage = (space = 20) => {
        if (y + space > pageHeight - 40) {
            doc.addPage();
            y = config.headerHeight + 10;
        }
    };

    /* HEADING */
    const headingText = "Minimum Guidelines to AIPL Inspectors";
    doc.setFont("Times", "bold");
    doc.setFontSize(16);
    headingText.split("\n").forEach((line) => {
        const w = doc.getTextWidth(line);
        doc.text(line, (pageWidth - w) / 2, y);
        y += 20;
    });
    doc.setLineWidth(1);
    doc.line(40, y, pageWidth - 40, y);
    y += 30;
    /* INTRO PARAGRAPHS */
    doc.setFont("Times", "normal");
    doc.setFontSize(12);

    const sections = [
        {
            title: "A] Requirements before start for Inspection site from residence or AIPL office:",
            type: "ol",
            items: [
                "To ask Operation Manager/Technical Manager/Inspection Coordinator for Inspection Notification (In form of mail or telephone etc.) & related documents received from Client or Buyer.",
                "To review & understand the requirements. In case of assistance, he should contact Operation Manager/Technical Manager or Senior Experienced Engineer from AIPL.",
                "To confirm readiness of Vendor before start of visit. This includes information on rules for the access and use of the specified facilities & Equipment necessary to perform inspections, its working condition and calibration status.",
                "To travelsafely & reach inspection location on time considering work timings of company to be visited.",
                "In case of 2 visits in a day, Inspector must calculate timings in co-ordination with Vendor rep. based on activities to be offered & accordingly inspection at another vendor to be carried out."
            ]
        },
        {
            title: "B] Requirements at inspection location:",
            type: "ol",
            items: [
                "To give ENTRY & EXIT calls in the form text message with time through SMS or any messenger app as per company policy.",
                "Monthly Invoice/Time Sheet must be submitted on last day of the working month or 1st day of the new month by 12 pm.",
                "Flash email must be sent ‘in reply’ to the inspection call email before leaving the shop, in case of Continued inspection on following day or for an Engineer if not possible to issue inspection report in same day by late evening. The contents of email must be activities of inspections carried out in short or simple understandable form.",
                "Follow the procedure at inspection site with respect to Environment, Health and Safety requirements, refer safety instruction to AIPL Inspector/Surveyors.",
                "Ensure availability and use of adequate PPE at the time of inspection.",
                "Check the Calibration Certificates of all measuring/testing equipment/instruments for validity and ensure that Calibration is available at the values of point-of-use (e.g. Power meter calibrated at Rated Voltage Point etc. and not only for range)."
            ]
        },
        {
            title: "C] Report submission deadline:",
            type: "p",
            items: ["The last deadline for submitting the inspection reports will be by next day 12 pm. Inspector is required to also submit the scan/photo of the diary he has written including any calculations done for the inspection as applicable."]
        },
        {
            title: "D] Types of Reports followed by AIPL:",
            type: "ol",
            items: [
                "Flash Report (FR):\nTo prepare for everyday reporting, If Inspection continues for more than one day. The FR must have Areas of concern if any, Vendor signature and 1 copy issued to them at shop itself.",
                "Inspection Report (IR):\nIt is our official Reporting format and to be made mentioning all details as per the Format applicable. Photos of Inspection shall be part of IR",
                "Inspection Release note (IRN):\nTo be issued based on the completion of inspection without any open punch points in IR to our office/admin along with Inspection Report."
            ]
        },
        {
            title: "E] Report Numbering Methodology:",
            type: "ul",
            items: [
                "Inspection Report : AIPL IR/AAAA/BBBB/CC/X",
                "Inspection Release Note : AIPL IRN/AAAA/BBBB/CC/X",
                "Flash Report : AIPL FR/AAAA/BBBB/CC/X",
                "AAAA – Client Name in max. 4 letters",
                "BBBB – Supplier/Vendor Name in max. 4 letters",
                "CC – Reporting AIPL Engineer’s name initials in max. 2 letters.",
                "X – Sr. No.",
                "GIVE NAME/NUMBER TO SOFT COPY FILE OF REPORT BY REPLACING ‘/’ WITH ‘-‘. e.g. Inspection Report AIPL IR-AAAA-BBBB-C",
                "In some case Client Reporting numbering methodology is used. (It will be communicated as applicable by office/admin)",
                "In case of any query, concern Engineer shall contact Operation Manager/Technical Manager for clarity."
            ]
        },
    ];

    sections.forEach((section) => {
        ensurePage(20);
        doc.setFont("Times", "bold");
        doc.setFontSize(12);
        doc.text(section.title, margin, y);
        y += 18;

        doc.setFont("Times", "normal");
        doc.setFontSize(11);

        if (section.type === "ol" || section.type === "ul") {
            section.items.forEach((item, i) => {
                ensurePage(15);
                const bullet = section.type === "ol" ? `${i + 1}. ` : "• ";
                const lines = doc.splitTextToSize(item, pageWidth - margin * 2 - 20);
                lines.forEach((line, idx) => {
                    doc.text(idx === 0 ? bullet + line : "    " + line, margin + 10, y);
                    y += lineHeight;
                });
            });
        } else if (section.type === "p") {
            section.items.forEach((item) => {
                const lines = doc.splitTextToSize(item, pageWidth - margin * 2);
                lines.forEach((line) => {
                    ensurePage(15);
                    doc.text(line, margin, y);
                    y += lineHeight;
                });
            });
        }

        y += 10;
    });
    /* =========================
     F] INSPECTION REPORT
  ========================= */
    ensurePage(30);
    doc.setFont("Times", "bold");
    doc.setFontSize(12);
    doc.text(
        "F] Mandatory Minimum things to be reported in the Inspection Report:",
        margin,
        y
    );
    y += 20;

    doc.setFont("Times", "normal");
    doc.setFontSize(11);

    const alpha = (i) => String.fromCharCode(97 + i); // a, b, c

    // (a)
    doc.splitTextToSize(
        "Item description: Client PO Sr. No, description and any special instructions to be reported in the remarks or notes in PO. Conclusion for Inspection of the Item i.e., acceptable / under hold / Rework and Reoffer etc. to be mentioned in ‘Remark’ Column for each Item.",
        460
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? `${alpha(0)}. ${line}` : line, idx === 0 ? 50 : 70, y);
        y += 15;
    });
    y += 5;

    // (b)
    doc.splitTextToSize(
        "Reference documents: Client Approved Documents only i.e. Purchase order, Drawing, ITP, Specification, Datasheet etc.",
        460
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? `${alpha(1)}. ${line}` : line, idx === 0 ? 50 : 70, y);
        y += 15;
    });
    y += 5;

    // (c) Inspection Activity
    doc.text(`${alpha(2)}. Inspection Activity:`, 50, y);
    y += 15;

    // 1.
    const inspectionActivity = [
        "Scope of Visit: The activities mentioned in inspection call or stages covered during the visit as per ITP. Mention details of other persons who attended/participated in the Inspection.",
        "Deviations: Mention deviations noticed at inspection site with respect to Environment, Health and Safety requirements, refer safety instruction to AIPL Inspector/Surveyors and write about it at Section 2 of ‘Inspection Activity’ details in Inspection Visit Report Format.\nIf no abnormality is observed; PLEASE MENTION: NIL.",
        "Details of inspection:"
    ];

    inspectionActivity.forEach((text, i) => {
        doc.splitTextToSize(text, 420).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? `${i + 1}. ${line}` : line, idx === 0 ? 80 : 100, y);
            y += 15;
        });
        y += 5;
    });

    // • bullet list
    const detailsList = [
        "Condition of Job/Inspection Item during Inspection.",
        "All observations of Inspection shall be made with reference to QAP/ITP Clauses as applicable.",
        "IR shall mention hard stamping done with locations of stamping.",
        "Random inspection samples Sr. No(s) / ID No(s) shall be recorded.",
        "Attachments such as witness & review documents shall be reported.",
    ];

    detailsList.forEach((item) => {
        doc.splitTextToSize(item, 400).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? "• " + line : line, idx === 0 ? 100 : 120, y);
            y += 15;
        });
    });

    // 2.
    const inspectionActivitysecondpara = [
        "Detail of Measuring/Testing Equipment and Calibration:\nEnsure that the measuring and Testing instruments/equipment is with valid Calibration Certificate traceable to national or international standards and in good working condition and should be handled safely while using it during Inspection activity.\nCalibration details of all instruments used for Inspection shall be mentioned with due date of calibration.",
        "Punch List: Shall be mentioned in case of any discrepancy observed during inspection.",
        "Photographs: Shall be part of Inspection Report (Photos to be taken progressively & shall be with names). For example"
    ];

    inspectionActivitysecondpara.forEach((text, i) => {
        doc.splitTextToSize(text, 420).forEach((line, idx) => {
            ensurePage(15);
            doc.text(idx === 0 ? `${i + 4}. ${line}` : line, idx === 0 ? 80 : 100, y);
            y += 15;
        });
        y += 5;

          // Insert GuidImg right after Photographs
    if (text.startsWith("Photographs")) {
        const imgWidth = 450; // adjust width as needed
        const imgHeight = 140; // adjust height as needed
        ensurePage(imgHeight + 20);
        doc.addImage(GuidImg, "PNG", 70, y, imgWidth, imgHeight);
        y += imgHeight + 15;
    }
    });

    // Remaining alpha points
    [
        "All supporting documents shall be reviewed as per the clauses of QAP/ITP and scanned copy of the documents shall be sent to the office/admin and in some cases one set of hard copies needs to be submitted.",
        "Inspector shall mention unique identification mark used to identify items inspected.",
        "Spell check shall be done before final submission of Report.",
        "Duly signed FR / IR / IRN with all supporting documents shall be emailed to office/admin.",
    ].forEach((text, i) => {
        doc.splitTextToSize(text, 460).forEach((line, idx) => {
            ensurePage(15);
            doc.text(
                idx === 0 ? `${alpha(i + 3)}. ${line}` : line,
                idx === 0 ? 50 : 60,
                y
            );
            y += 15;
        });
        y += 5;
    });

    /* =========================
       G] FLASH REPORT
    ========================= */
    ensurePage(30);
    doc.setFont("Times", "bold");
    doc.setFontSize(12);
    doc.text(
        "G] Mandatory Minimum things to be reported in the Flash Report:",
        margin,
        y
    );
    y += 20;

    doc.setFont("Times", "normal");
    doc.setFontSize(11);

    /* (a) Purchase Order */
    doc.splitTextToSize(
        "Purchase Order/Reference documents: Client Approved Documents only i.e Purchase order, Drawing, ITP, Specification, Datasheet etc.",
        460
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? "a. " + line : line, idx === 0 ? 50 : 70, y);
        y += 15;
    });
    y += 5;

    /* (b) Supplier Details */
    doc.splitTextToSize(
        "Supplier Details: Supplier Name & address, contact person name & contact details.",
        460
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? "b. " + line : line, idx === 0 ? 50 : 70, y);
        y += 15;
    });
    y += 5;

    /* (c) Inspection Activity */
    doc.setFont("Times", "bold");
    doc.text("c. Inspection Activity:", 50, y);
    y += 15;

    doc.setFont("Times", "normal");

    /* 1. Detail of inspection */
    const flashInspectionDetails = [
        "Detail of inspection:\nAreas of Concern if any\nCondition of Job/Inspection Item during Inspection.\nAll observations of Inspections shall be recorded as per clauses of ITP.",
    ];

    flashInspectionDetails.forEach((text, i) => {
        doc.splitTextToSize(text, 420).forEach((line, idx) => {
            ensurePage(15);
            doc.text(
                idx === 0 ? `1.${i === 0 ? "" : ""} ${line}` : line,
                idx === 0 ? 80 : 100,
                y
            );
            y += 15;
        });
    });

    /* 2. Deviations */
    doc.splitTextToSize(
        "Mention deviations noticed at inspection site with respect to Environment, Health and Safety requirements, refer safety instruction to AIPL Inspector/Surveyors and write about it at Section 2 of ‘Inspection Activity’ details in Flash Report Format.\nIf no abnormality is observed; PLEASE MENTION: NIL",
        420
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? "2. " + line : line, idx === 0 ? 80 : 100, y);
        y += 15;
    });
    y += 5;

    /* 3. Balance activities */
    doc.splitTextToSize(
        "Balance activities: Flash Report shall clearly mention balance stages of Inspection as per clauses of ITP.",
        420
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? "3. " + line : line, idx === 0 ? 80 : 100, y);
        y += 15;
    });
    y += 5;

    /* 4. Next Inspection Date */
    doc.splitTextToSize(
        "Indicate Next Date of Inspection, if planned.",
        420
    ).forEach((line, idx) => {
        ensurePage(15);
        doc.text(idx === 0 ? "4. " + line : line, idx === 0 ? 80 : 100, y);
        y += 15;
    });

    // Add Prepared by / Approved by section/*
    ensurePage(130);
    y += 20;
    doc.setFont("Times", "bold");
    doc.setFontSize(12);
    // Headings
    doc.text("Prepared by", 80, y);
    doc.text("Approved By", pageWidth / 2 + 60, y);
    y += 10;
    // Images using addImage
    doc.addImage(SignImgMadhav, "PNG", 80, y, 120, 50);
    doc.addImage(SignRamesh, "PNG", pageWidth / 2 + 60, y, 120, 55);
    y += 60;
    doc.setFont("Times", "normal");
    doc.text("Madhav Naik", 80, y);
    doc.text("Ramesh S Jadhav", pageWidth / 2 + 60, y);
    y += 15;
    doc.text("IMC", 80, y);
    doc.text("CEO & Managing Director", pageWidth / 2 + 60, y);
};

// PAGE 4 Employee Handbook
const generateEmployeeHandbookPage = (doc) => {
  // Start content below header using config.headerHeight
  let y = config.headerHeight + 10;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const leftMargin = 40;
  const rightMargin = 40;
  const maxWidth = pageWidth - leftMargin - rightMargin;

  // Helper function to check and add new page
  const ensurePage = (requiredSpace = 100) => {
    if (y > pageHeight - config.footerHeight - requiredSpace) {
      doc.addPage();
      addPageHeader(doc, doc.getNumberOfPages());
      y = config.headerHeight + 10;
      return true;
    }
    return false;
  };

  // 🔹 Add Center Heading
    /* HEADING */
    const MainheadingText = "Employee Handbook";
    doc.setFont("Times", "bold");
    doc.setFontSize(16);
    MainheadingText.split("\n").forEach((line) => {
        const w = doc.getTextWidth(line);
        doc.text(line, (pageWidth - w) / 2, y);
        y += 20;
    });
    doc.setLineWidth(1);
    doc.line(40, y, pageWidth - 40, y);
    y += 30;

  doc.setFont("Times", "bold");
  doc.setFontSize(14);
  const headingText = "(Employee Code of Conduct/ Ethics policy Document)";
  const textWidth = doc.getTextWidth(headingText);
  doc.text(headingText, (pageWidth - textWidth) / 2, y);
  
  // Draw underline
  doc.setLineWidth(0.5);
  doc.line((pageWidth - textWidth) / 2, y + 2, (pageWidth + textWidth) / 2, y + 2);
  y += 25;

  // Company Name
  doc.setFont("Times", "bold");
  doc.setFontSize(12);
  doc.text("DYC Global Inspections Pvt Ltd", leftMargin, y);
  y += 25;

  // Helper function to add section
  const addSection = (title, content) => {
    ensurePage(80);

    doc.setFont("Times", "bold");
    doc.setFontSize(11);
    doc.text(title, leftMargin, y);
    y += 15;

    if (content) {
      doc.setFont("Times", "normal");
      doc.setFontSize(10.5);
      const lines = doc.splitTextToSize(content, maxWidth);
      doc.text(lines, leftMargin, y);
      y += (lines.length * 12) + 10;
    }
  };

  // 1. Purpose of this Handbook
  addSection(
    "1. Purpose of this Handbook:",
    "This handbook has been prepared to inform new employees of the policies and procedures of DYC Global Inspections Pvt. Ltd. and to establish the company's expectations. It is not all inclusive or intended to provide strict interpretations of our policies; rather, it offers an overview of the work environment. This handbook is not a contract, expressed or implied, guarantying employment for any length of time and is not intended to induce an employee to accept employment with the company.\n\nThe company reserves the right to unilaterally revise, suspend, revoke, terminate or change any of its policies, in whole or in part, whether described within this handbook or elsewhere, in its sole discretion. If any discrepancy between this handbook and current company policy arises, conform to current company policy. Every effort will be made to keep you informed of the company's policies. Feel free to ask questions about any of the information within this handbook."
  );

  // 2. History, Goals & Culture
  ensurePage(150);

  doc.setFont("Times", "bold");
  doc.setFontSize(11);
  doc.text("2. History, Goals & Culture:", leftMargin, y);
  y += 15;

  doc.setFont("Times", "italic");
  doc.text("About DYC Global Inspections Pvt Ltd", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);
  let content = "DYC Global Inspections Pvt Ltd provides inspection, expediting, NDT services and industrial consulting services to various clients in India & aboard. With its expertise in offering Individual solutions based on customer's requirements, DYC Global is making its mark in various segments which includes Oil & Gas (offshore installations as well as onshore refineries), Petrochemical Chemical, Fertilizer, Power, Steel, Infrastructure & many more.\n\nWe are having associates within various states of India as well as Italy, Germany, UK and Malaysia.";
  let lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 20;

  // Our Vision
  ensurePage(80);
  doc.setFont("Times", "bold");
  doc.text("Our Vision", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  content = "Our vision is to be expert service provider in the field of inspection, expediting & training in enabling industries & business to achieve highest performance whilst assuring excellence and quality, ethics, integrity, health, safety & environment with social consciousness & commitment in the delivery of their products and our services.";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 20;

  // Quality Policy
  ensurePage(120);
  doc.setFont("Times", "bold");
  doc.text("Quality Policy", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  content = "DYC Global Inspections Pvt Ltd is committed to provide third party inspection, expediting and training services by;";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 10;

  // Quality Policy Points
  const qualityPoints = [
    "Consistently meeting and exceeding customer's expectations of service quality and performance.",
    "Timely completion of services to meet our customer's requirements within affordable cost",
    "Continuous improvement of our processes and systems by using advanced techniques"
  ];

  qualityPoints.forEach((point, index) => {
    ensurePage(20);
    const pointLines = doc.splitTextToSize(`${index + 1}. ${point}`, maxWidth - 10);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 12) + 5;
  });

  y += 10;

  // 3. Hierarchy
  addSection("3. Hierarchy DYC Global Inspections Pvt Ltd:", "");

  // 4. Equal Opportunity Employment
  addSection(
    "4. Equal Opportunity Employment",
    "This company is an equal opportunity employer and does not unlawfully discriminate against employees or applicants for employment on the basis of an individual's race, colour, religion, creed, sex, national origin, age, disability, marital status, veteran status or any other status protected by applicable law. This policy applies to all terms, conditions and privileges of employment, including recruitment, hiring, placement, compensation, promotion, discipline and termination."
  );

  // 5. Policy Against Workplace Harassment
  ensurePage(200);

  doc.setFont("Times", "bold");
  doc.setFontSize(11);
  doc.text("5. Policy Against Workplace Harassment", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);
  content = "This company prohibits discrimination or harassment based on race, colour, religion, creed, sex, national origin, age, disability, marital status, veteran status or any other status protected by applicable law. Each individual has the right to work in a professional atmosphere that promotes equal employment opportunities and is free from discriminatory practices, including without limitation harassment. Consistent with its workplace policy of equal employment opportunity, the company prohibits and will not tolerate harassment on the basis of race, colour, religion, creed, sex, national origin, age, disability, marital status, veteran status or any other status protected by applicable law. Violations of this policy will not be tolerated.\n\nDiscrimination includes, but is not limited to: making any employment decision or employment related action on the basis of race, colour, religion, creed, age, sex, disability, national origin, marital or veteran status, or any other status protected by applicable law.\n\nSexual harassment is defined as unwelcome sexual advances, requests for sexual favours and other verbal, visual or physical conduct of a sexual nature, when:";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 10;

  const harassmentPoints = [
    "Submission to such conduct is made either explicitly or implicitly a term or condition of an individual's employment;",
    "Submission to or rejection of such conduct by an individual is used as the basis for employment decisions affecting such individual; or",
    "Such conduct has the purpose or effect of unreasonably interfering with an individual's work performance or creating an intimidating, hostile or offensive working environment."
  ];

  harassmentPoints.forEach((point, index) => {
    ensurePage(30);
    const pointLines = doc.splitTextToSize(`${index + 1}. ${point}`, maxWidth - 20);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 12) + 5;
  });

  y += 15;

  // 6. Type Of Employments
  ensurePage(150);
  doc.setFont("Times", "bold");
  doc.setFontSize(11);
  doc.text("6. Type Of Employments", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);

  const employmentTypes = [
    "Definition of Terms:",
    "Employer:",
    "The DYC Global is the employer of all full-time part-time and temporary employees. An employee is hired, provided compensation and applicable benefits, and has his or her work directed and evaluated by DYC Global.",
    "On payroll employee: A Full Time Employee regularly works at 6 day a week.",
    "Contract Employee: They are often considered to be self-employed, and have the freedom to pick and choose their projects and companies they would like to be associated with"
  ];

  employmentTypes.forEach(item => {
    ensurePage(25);
    const itemLines = doc.splitTextToSize(` ${item}`, maxWidth - 15);
    doc.text(itemLines, leftMargin + 5, y);
    y += (itemLines.length * 12) + 5;
  });

  y += 15;

  // 7. Hours Of Work, Attendance And Punctuality
  ensurePage(60);
  doc.setFont("Times", "bold");
  doc.setFontSize(11);
  doc.text("7. Hours Of Work, Attendance And Punctuality", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);
  const workHours = doc.splitTextToSize("• Employees who on client location they have to follow working timing as per the client companies norms.", maxWidth - 15);
  doc.text(workHours, leftMargin + 5, y);
  y += (workHours.length * 12) + 15;

  // 8. Dress Code and Public Image
  ensurePage(80);
  doc.setFont("Times", "bold");
  doc.text("8. Dress Code and Public Image", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  const dressCodePoints = [
    "Employees should wear clean, ironed and neat clothes.",
    "Keep your hair neat, nails trimmed, and maintain good personal hygiene."
  ];

  dressCodePoints.forEach(point => {
    ensurePage(20);
    const pointLines = doc.splitTextToSize(`• ${point}`, maxWidth - 15);
    doc.text(pointLines, leftMargin + 5, y);
    y += (pointLines.length * 12) + 5;
  });

  y += 15;

  // 9. Attendance and Punctuality
  addSection(
    "9. Attendance and Punctuality",
    "It is important for you to report to work on time and to avoid unnecessary absences. The Company recognizes that illness or other circumstances beyond your control may cause you to be absent from work from time to time. However, frequent absenteeism or tardiness may result in disciplinary action, up to and including discharge. Excessive absenteeism or frequent tardiness can have a negative impact on the success of the Company."
  );

  // 10. Workspace
  addSection(
    "10. Workspace",
    "Employees should keep in mind that their workspace is part of a professional environment that portrays the Company's overall dedication to providing quality service to its clients. Therefore, your workspace should be clean, organized and free of items that are not required to perform your job."
  );

  // 11. Company Property
  ensurePage(150);
  doc.setFont("Times", "bold");
  doc.setFontSize(11);
  doc.text("11. Company Property", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);

  const companyPropertyPoints = [
    "For Inspection Client company provide their hard and soft stamp to Engineer. Employees have to use stamp only for official use. Personal use of the stamps is illegal.",
    "Employees have to returns client companies property after completion of project or Employment.",
    "Employees are reminded that they should have no expectation of privacy in their use of company computers or other equipment.",
    "Employees are reminded that they should have no expectation of privacy in their use of company computers or other electronic equipment.",
    "Violations of these policies could result in disciplinary action."
  ];

  companyPropertyPoints.forEach((point, index) => {
    ensurePage(25);
    const pointLines = doc.splitTextToSize(`${index + 1}. ${point}`, maxWidth - 15);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 12) + 5;
  });

  y += 15;

  // 12. Personnel Files
  addSection(
    "12. Personnel Files",
    "The company maintains a personnel file on each employee. These files are kept Confidential to the extent possible. Employees may review their personnel file upon Request. It is important that personnel files accurately reflect each employee's personal Information. Employees are expected to inform the company of any change in name, Address, home phone number, home address, marital status, number of dependents or emergency contact information"
  );

  // 13. Workplace Safety
  addSection(
    "13. Workplace Safety",
    "Employees should use all safety and protective equipment provided to them, and maintain work areas in a safe and orderly manner, free from hazardous conditions. Employees who observe an unsafe practice or condition should report it to a supervisor or concern person. Employees are prohibited from making threats against anyone in connection with his/her work or engaging in violent activities while in the employ of the company.\n\nEmployees are encouraged to always remain aware of their surroundings and to exercise caution while traveling. Prioritize safety by utilizing trusted transportation and accommodations. Stay informed about potential risks and adhere to company travel guidelines at all times."
  );

  // 14. Compensation and Benefits
  ensurePage(100);
  doc.setFont("Times", "bold");
  doc.setFontSize(11);
  doc.text("14. Compensation and Benefits", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);
  content = "Compensation is the monetary benefit which is given to an employee or worker giving their services to an organization. Compensation includes components like salary, wages, bonuses etc. The compensation provided helps in motivating the employees, build their career and ensure that there are committed in achieving the company goals.";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 15;

  // Deductions
  ensurePage(60);
  doc.setFont("Times", "bold");
  doc.text("Deductions:", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  content = "TDS of Contract Employee are deducted as per law. They get certificates also for the same.";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 15;

  // 15. Exit Policy
  addSection(
    "15. Exit Policy",
    "Valuable feedback concerning streamlining and strengthening processes to improve employee engagement and retention can be obtained during the Exit Interview. The Exit Interview is designed to solicit ideas, suggestions and experience of employees leaving the company as to how DYC Global Inspections may be made better place to work.\n\nFor more details refer separate \"Exit policy\".\n\nIf you have any questions regarding any of the policy guidelines listed above, please contact HR Dept."
  );

  // Horizontal Line
  ensurePage(80);
  doc.setLineWidth(1);
  doc.line(leftMargin, y, pageWidth - rightMargin, y);
  y += 20;

  // Quality, Health, Safety & Environment Policy
  ensurePage(200);
  doc.setFont("Times", "bold");
  doc.setFontSize(12);
  const qhseTitle = "Quality, Health, Safety & Environment Policy";
  const qhseTitleWidth = doc.getTextWidth(qhseTitle);
  doc.text(qhseTitle, (pageWidth - qhseTitleWidth) / 2, y);
  y += 20;

  doc.setFont("Times", "normal");
  doc.setFontSize(10.5);
  content = "We at DYC Global Inspections Pvt Ltd believe that our Business Sustenance depends on our continued quality of services while protecting People and the Environment.\n\nWe are committed to meet our Clients' Quality expectations also along with Safe and Healthy Environment.\n\nIn view of this Vision, we consistently keep below Mission activities in our focus:";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 12) + 10;

  // QHSE Mission Points
  const qhsePoints = [
    "Recruiting competent Engineers who are Safety Conscious and demonstrate their respect towards Health and Environment",
    "Minimizing adverse impact of our activities to the Environment.",
    "Optimum utilization of Energy, Machinery, Finance and other resources",
    "Strong Organizational Support through human resource development for Emergency Preparedness.",
    "Provision of Safe working environment for prevention of injury.",
    "Communicating openly with Client's and Vendors for making them aware of QHSE aspects of our activities",
    "Compliance with Client's agreed Procedures and Systems",
    "Compliance of applicable Legal and other requirements."
  ];

  qhsePoints.forEach(point => {
    ensurePage(20);
    const pointLines = doc.splitTextToSize(`• ${point}`, maxWidth - 15);
    doc.text(pointLines, leftMargin + 5, y);
    y += (pointLines.length * 12) + 5;
  });
  y += 15;
  // CEO Signature
  ensurePage(80);
  doc.addImage(SignRamesh, "PNG", pageWidth - rightMargin - 120, y, 120, 60);
  y += 65;
  doc.setFont("Times", "bold");
  doc.text("Ramesh S. Jadhav", pageWidth - rightMargin - 100, y);
  y += 15;
  doc.text("Chief Executive Officer", pageWidth - rightMargin - 120, y);
  y += 15;
  doc.text("1st September 2020", pageWidth - rightMargin - 110, y);
  y += 30;
};

// page 5 CONTENT - IMS-QEHS POLICY
const generateIMSQEHSPolicyPage = (doc, employeeName = "", inspectSign, inspectorpolicDate) => {
  // Start content below header using config.headerHeight
  let y = config.headerHeight + 10;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const leftMargin = 40;
  const rightMargin = 40;
  const maxWidth = pageWidth - leftMargin - rightMargin;

  // Helper function to check and add new page
  const ensurePage = (requiredSpace = 100) => {
    if (y > pageHeight - config.footerHeight - requiredSpace) {
      doc.addPage();
      addPageHeader(doc, doc.getNumberOfPages());
      y = config.headerHeight + 10;
      return true;
    }
    return false;
  };

  // Document Info Header (Right aligned)
  doc.setFont("Times", "normal");
  doc.setFontSize(9);
  
  const infoX = pageWidth - rightMargin - 150;
  doc.setFont("Times", "bold");
  doc.text("Format No:", infoX, y);
  doc.setFont("Times", "normal");
  doc.text("AIPL/IMS/IMC/5.2", infoX + 55, y);
  y += 12;

  doc.setFont("Times", "bold");
  doc.text("Issued on:", infoX, y);
  doc.setFont("Times", "normal");
  doc.text("05-10-2020", infoX + 55, y);
  y += 12;

  doc.setFont("Times", "bold");
  doc.text("Rev. No. & Date:", infoX, y);
  doc.setFont("Times", "normal");
  doc.text(" 01 | 01-08-2023", infoX + 70, y);
  y += 12;

  doc.setFont("Times", "bold");
  doc.text("Clause No.:", infoX, y);
  doc.setFont("Times", "normal");
  doc.text("5.2", infoX + 55, y);
  y += 30;

  // Main Heading
  doc.setFont("Times", "bold");
  doc.setFontSize(16);
  const mainHeading = "DYC Global INSPECTIONS PVT. LTD.";
  const mainHeadingWidth = doc.getTextWidth(mainHeading);
  doc.text(mainHeading, (pageWidth - mainHeadingWidth) / 2, y);
  y += 25;

  // Subheading
  doc.setFont("Times", "bold");
  doc.setFontSize(13);
  const subHeading = "IMS-QEHS POLICY";
  const subHeadingWidth = doc.getTextWidth(subHeading);
  doc.text(subHeading, (pageWidth - subHeadingWidth) / 2, y);
  
  // Underline subheading
  doc.setLineWidth(0.5);
  doc.line((pageWidth - subHeadingWidth) / 2, y + 2, (pageWidth + subHeadingWidth) / 2, y + 2);
  y += 20;

  // Subtitle (Italic)
  doc.setFont("Times", "italic");
  doc.setFontSize(11);
  const subtitle = "(Integrated Management System – Quality – Environment – Occupational Health and Safety)";
  const subtitleWidth = doc.getTextWidth(subtitle);
  doc.text(subtitle, (pageWidth - subtitleWidth) / 2, y);
  y += 25;

  // Main paragraph
  doc.setFont("Times", "normal");
  doc.setFontSize(11);
  let content = "We, at DYC Global Inspections Pvt. Ltd., are committed to satisfy both the stated & implied needs and expectations of the relevant interested parties including customers by providing products, processes and services which are of high quality, environmental friendly and safe.";
  let lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 13) + 15;

  // "We strive to,"
  ensurePage(100);
  doc.setFont("Times", "bold");
  doc.text("We strive to,", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  const strivePoints = [
    "Comply with the applicable requirements, compliance obligations and other requirements to which our organisation subscribes.",
    "Continual and sustainable improvement of the Integrated Management system (IMS-QEHS) with risk based approach to enhance the organizational performance on QEHS."
  ];

  strivePoints.forEach((point, index) => {
    ensurePage(30);
    const letter = String.fromCharCode(97 + index); // a, b, c...
    const pointLines = doc.splitTextToSize(`${letter}) ${point}`, maxWidth - 20);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 13) + 5;
  });

  y += 10;

  // "We do commit to prevent,"
  ensurePage(80);
  doc.setFont("Times", "bold");
  doc.text("We do commit to prevent,", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  const preventPoints = [
    "The environmental pollution in all our activities, products and services considering a life cycle perspective;",
    "The occupational ill-health and injury taking opportunity into consideration."
  ];

  preventPoints.forEach((point, index) => {
    ensurePage(25);
    const letter = String.fromCharCode(97 + index);
    const pointLines = doc.splitTextToSize(`${letter}) ${point}`, maxWidth - 20);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 13) + 5;
  });

  y += 10;

  // "We do ensure,"
  ensurePage(120);
  doc.setFont("Times", "bold");
  doc.text("We do ensure,", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  const ensurePoints = [
    "Protecting the environment relevant to the context of our organisation.",
    "Sustainable resource use.",
    "Reducing – Reusing and Recycling (R-R-R) of wastage.",
    "Training for all-round development of our employees.",
    "Providing and maintaining safe, healthy and environmental friendly work places for employee and other relevant interested parties through operational procedures, systems and work methods."
  ];

  ensurePoints.forEach((point, index) => {
    ensurePage(25);
    const letter = String.fromCharCode(97 + index);
    const pointLines = doc.splitTextToSize(`${letter}) ${point}`, maxWidth - 20);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 13) + 5;
  });

  y += 15;

  // Objectives paragraph
  ensurePage(60);
  content = "In line with this Policy, DYC Global Inspections Pvt. Ltd. does establish IMS-QEHS Objectives for each process, which are appropriate and supporting the purpose, context and strategic direction of the Organization.";
  lines = doc.splitTextToSize(content, maxWidth);
  doc.text(lines, leftMargin, y);
  y += (lines.length * 13) + 15;

  // "We shall,"
  ensurePage(80);
  doc.setFont("Times", "bold");
  doc.text("We shall,", leftMargin, y);
  y += 15;

  doc.setFont("Times", "normal");
  const shallPoints = [
    "Communicate and apply this policy within the organisation.",
    "Make this policy available to the interested parties.",
    "Review this policy periodically for its relevance and appropriateness."
  ];

  shallPoints.forEach((point, index) => {
    ensurePage(20);
    const letter = String.fromCharCode(97 + index);
    const pointLines = doc.splitTextToSize(`${letter}) ${point}`, maxWidth - 20);
    doc.text(pointLines, leftMargin + 10, y);
    y += (pointLines.length * 13) + 5;
  });

  y += 30;

  // Signature Section
  ensurePage(120);  
  // Date on left
  doc.setFont("Times", "bold");
  doc.text("Date:", leftMargin, y);
  doc.setFont("Times", "normal");
  doc.text("02-08-2024", leftMargin + 35, y);
  // CEO Signature on right
  const signX = pageWidth - rightMargin - 120;
  // Add CEO signature image
  doc.addImage(SignRamesh, 'PNG', signX, y - 30, 120, 60);
  y += 40;  
  // CEO title
  doc.setFont("Times", "bold");
  doc.setFontSize(10);
  doc.text("Ramesh S Jadhav", signX + 30, y);
  doc.text("CEO & MANAGING DIRECTOR", signX - 10, y+15);
};
